// Controls
// Styles
import { getClassNames, getStyles } from "./VirtualListWrapper.styles";
// Utilities
import { css } from "@fluentui/utilities/lib/css";
import React, { useCallback, useState } from "react";
import { InView } from "react-intersection-observer";
export var VirtualListOrientation;
(function(VirtualListOrientation) {
    VirtualListOrientation["Vertical"] = "vertical";
    VirtualListOrientation["Horizontal"] = "horizontal";
})(VirtualListOrientation || (VirtualListOrientation = {}));
/**
 * VirtualListWrapper is a Virtualization helper component that concatenates an observer div after the children elements and calls callback when the element comes into view.
 *
 * How to use it:
 *
 * BEFORE:
 * - For a component that renders a full list
 *       List() {
 *          return items.forEach(item => SingleItem(item));
 *       }
 *
 * AFTER:
 * - The wrapping component still controls paging logic which looks like this:
 * - VirtualListWrapper needs callback onLastElementIsVisible which should increment the paging size
 * - Property hasMoreItemsToRender indicates if still needs to render observer element, parent wrapper sets to false once it knows that the full list is rendered
 *       List() {
 *          const itemsToRender = items.slice(0, this.state.numItemsToRender);
 *          return (<VirtualListWrapper =
 *                      id="myComponent"
 *                      hasMoreItemsToRender= {this.state.numItemsToRender < items.length}
 *                      onLastElementIsVisible= { this.setState({ this.state.numItemsToRender: this.state.numItemsToRender + PAGE_INCREMENT_SIZE})}>
 *                    { itemsToRender.forEach(item => renderSingleItem(item)) }
 *                  </VirtualListWrapper>
 *                 )
 *       }
 */ export function VirtualListWrapper({ id, hasMoreItemsToRender, orientation, onLastElementIsVisible, children, reversed = false, styles }) {
    const [state, setState] = useState({
        keyIndex: 1
    });
    const clientHeight = document?.documentElement ? document.documentElement.clientHeight : 0;
    const classNames = getClassNames(getStyles(orientation, styles));
    const handleInViewCallback = useCallback((inView)=>{
        if (inView) {
            onLastElementIsVisible();
            // Updates observer key it can handle view updates
            setState((prev)=>({
                    keyIndex: prev.keyIndex + 1
                }));
        }
    }, [
        onLastElementIsVisible
    ]);
    // The key on the Observer element was added to force recreation of the element. When scrolling (extremely) quickly, the onChange event wasn't being called.
    // This is because the user had scrolled to the pivot before inView was determined by the library and onChange wasn't fired. With the key, when the
    // number of cards to render has changed, the element will be recreated and the current value of inView will be determined which will fire the onChange event.
    // For reversed mode, observer is rendered before children (top), otherwise after (bottom)
    const observer = hasMoreItemsToRender ? /*#__PURE__*/ React.createElement("div", {
        className: `${id}IntersectionPivot virtualListIntersectionPivot ${orientation}${reversed ? " reversed" : ""}`,
        key: `${id}IntersectionObserverKey`
    }, /*#__PURE__*/ React.createElement(InView, {
        onChange: handleInViewCallback,
        rootMargin: clientHeight + "px 0px " + clientHeight + "px 0px",
        key: `${id}Key${state.keyIndex}`
    }, ({ ref })=>/*#__PURE__*/ React.createElement("div", {
            ref: ref,
            className: css(`${id}IntersectionObserverAnchor`, classNames.anchor)
        }))) : undefined;
    return /*#__PURE__*/ React.createElement(React.Fragment, null, reversed && observer, children, !reversed && observer);
}
